import React, { memo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
  Theme,
  useMediaQuery,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import RefreshIcon from '@material-ui/icons/Refresh';

import type { IqeProject } from '../../interfaces/ui';
import EnhanceTestPlanModal from './EnhanceTestPlanModal';
import StatusBadge from '../../common/StatusBadge';
import EnvironmentBadge from '../../common/EnvironmentBadge';
import TestExecutionActionDropdown from '../../common/TestExecutionActionDropdown';
import { WarningIcon } from '../../icons/WarningIcon';
import {
  getProjectExecutionActionState,
  type TestExecutionActionKey,
} from '../../utils/projectExecutionActions';

import { useStyles } from '../../styles';
import '../../styles/projectCard.css';
import '../../styles/enhanceTestPlanModal.css';
import { getProjectStatusMessage } from '../../utils';

interface ProjectCardProps {
  project: IqeProject;
  isToolAdmin: boolean;
  isRefreshingStatus: boolean;
  onRefreshStatus: (projectId: string) => void;
  onGenerateTests: (projectId: string) => void;
  onViewCurrentStatus: (
    projectId: string,
    projectName: string,
    transactionId: string,
  ) => void;
  onRegenerateTests: (projectId: string) => void;
  onRunTests: (projectId: string) => void;
  onViewResults: (projectId: string) => void;
  isRerunning: boolean;
  onRerunValidation: (projectId: string) => void;
  onViewProjectDetails?: (projectId: string) => void;
}

const ProjectCard = ({
  project,
  isToolAdmin,
  isRefreshingStatus,
  onRefreshStatus,
  onGenerateTests,
  onViewCurrentStatus,
  onRegenerateTests,
  onRunTests,
  onViewResults,
  isRerunning,
  onRerunValidation,
  onViewProjectDetails,
}: ProjectCardProps) => {
  const classes = useStyles();
  const isMobile = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'), {
    noSsr: true,
  });
  const [showEnhanceModal, setShowEnhanceModal] = useState(false);
  const hasTestAssets = project.runs && project.runs.length > 0;

  const isFailed = ['FAILED', 'FAILED_LOGIN'].includes(project.status);

  const isInProgress = [
    'SUBMITTED',
    'PROCESSING',
    'GENERATING',
    'EXECUTING',
    'ENHANCING',
  ].includes(project.status);

  const isExecutionInProgress = [
    'GENERATING',
    'EXECUTING',
    'ENHANCING',
  ].includes(project.status);

  const disableGenerateButton = !isToolAdmin || isInProgress;

  const { displayVersion, hasLastRunData, options } =
    getProjectExecutionActionState(project.runs, isExecutionInProgress);
  const shouldShowRetryGeneration =
    project.status === 'GENERATE_FAILED' && !hasLastRunData;
  const isFailureCard = isFailed || shouldShowRetryGeneration;

  const handleActionSelect = (action: TestExecutionActionKey) => {
    switch (action) {
      case 'GENERATE':
        onGenerateTests(project.id);
        break;
      // case 'viewCurrentGeneration':
      //   onViewCurrentGeneration(project.id);
      //   break;
      case 'RUN':
        onRunTests(project.id);
        break;
      case 'ENHANCE':
        setShowEnhanceModal(true);
        break;
      default:
        break;
    }
  };

  return (
    <Box className={`pc-card ${classes.projectCard}`}>
      {/* Header */}
      <Box className={`pc-header${isMobile ? ' pc-header-mobile' : ''}`}>
        <Box className="pc-title-area">
          <Typography variant="h6" className="pc-name">
            {project.name}
          </Typography>
          <Box className="pc-badge-group">
            <EnvironmentBadge environment={project.environment} />
            <StatusBadge status={project.status} />
          </Box>
          {isInProgress && (
            <CardActionIconButton
              title="Refresh status"
              icon={
                <RefreshIcon
                  className={isRefreshingStatus ? 'pc-icon-spin' : undefined}
                />
              }
              onClick={() => onRefreshStatus(project.id)}
              disabled={!isToolAdmin || isRefreshingStatus}
            />
          )}
        </Box>
      </Box>

      {/* Details row — always visible */}
      <Box
        className={`pc-details-row${isMobile ? ' pc-details-row-mobile' : ''}`}
      >
        <Box className="pc-detail-item">
          <Typography component="span" className="pc-detail-label">
            Non-Prod App URL
          </Typography>
          <Typography
            component="span"
            className={`pc-detail-value${
              isMobile ? ' pc-detail-url-value-mobile' : ''
            }`}
          >
            {project.appUrl}
          </Typography>
        </Box>
        <Box className="pc-detail-item">
          <Typography component="span" className="pc-detail-label">
            Test Plan Name
          </Typography>
          <Typography component="span" className="pc-detail-value">
            {project.testPlanName}
          </Typography>
        </Box>
      </Box>

      {/* Failed State */}
      {isFailureCard && (
        <>
          <Box className="pc-error-compact">
            <Box className="pc-error-icon-box">
              <WarningIcon />
            </Box>
            <Typography component="p" className="pc-error-text">
              {shouldShowRetryGeneration
                ? 'Test generation failed. Please retry generation.'
                : project.validationError ||
                  'Unable to validate project. Please check the Non-Prod App URL and credentials.'}
            </Typography>
          </Box>

          <Box className="pc-next-steps-section">
            <Typography component="span" className="pc-section-label">
              Next Steps
            </Typography>
            {shouldShowRetryGeneration ? (
              <ol className="pc-next-steps-list">
                <li>Verify the Non-Prod App URL is correct and accessible</li>
                <li>Verify test plan and suite mappings are valid</li>
                <li>Retry generation</li>
              </ol>
            ) : (
              <ol className="pc-next-steps-list">
                <li>Verify the Non-Prod App URL is correct and accessible</li>
                <li>
                  Verify credentials are valid and have proper permissions
                </li>
                <li>Save any project changes</li>
                <li>Retry validation</li>
              </ol>
            )}
          </Box>

          <Box className="pc-error-recovery">
            <Button
              variant="contained"
              className="pc-retry-btn"
              onClick={() =>
                shouldShowRetryGeneration
                  ? onGenerateTests(project.id)
                  : onRerunValidation(project.id)
              }
              startIcon={
                <RefreshIcon
                  className={isRerunning ? 'pc-icon-spin' : undefined}
                />
              }
              disabled={
                shouldShowRetryGeneration
                  ? disableGenerateButton
                  : !isToolAdmin || isRerunning
              }
            >
              {shouldShowRetryGeneration
                ? 'Retry Generation'
                : 'Retry Validation'}
            </Button>
          </Box>
        </>
      )}

      {/* Ready State Content */}
      {!isFailureCard && (
        <>
          <Box
            className={`pc-content${!hasTestAssets ? ' pc-content-empty' : ''}`}
          >
            <Box
              className={`pc-kpi-metrics${
                isMobile ? ' pc-kpi-metrics-mobile' : ''
              }`}
            >
              {[
                {
                  label: 'Last Run',
                  value: displayVersion
                    ? displayVersion.createdAt?.toLocaleDateString()
                    : 'Never',
                },
                {
                  label: 'Test Cases',
                  value: displayVersion ? displayVersion.testCases : '__',
                },
                {
                  label: 'Pass Rate',
                  value: displayVersion ? `${displayVersion.passRate}%` : '__',
                },
                {
                  label: 'Failed',
                  value: displayVersion ? displayVersion.failed : '__',
                },
              ].map(({ label, value }) => (
                <Box
                  key={label}
                  className={`pc-kpi-chip${
                    !displayVersion ? ' pc-kpi-chip-placeholder' : ''
                  }`}
                >
                  <Typography
                    component="span"
                    variant="caption"
                    className="pc-kpi-label"
                  >
                    {label}
                  </Typography>
                  <Typography
                    component="span"
                    className={`pc-kpi-value${
                      !displayVersion ? ' pc-kpi-value-placeholder' : ''
                    }`}
                  >
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              component="p"
              variant="body1"
              className="pc-empty-state-text"
            >
              {isExecutionInProgress && (
                <CircularProgress size={16} className="pc-icon-spin" />
              )}
              {getProjectStatusMessage(project.status)}
            </Typography>

            <Box className="pc-actions">
              <Box
                className={`pc-actions-row${
                  isMobile ? ' pc-actions-row-mobile' : ''
                }`}
              >
                {/* {hasTestAssets && ( 
                  <Button
                    variant="outlined"
                    color="primary"
                    className="pc-btn-secondary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => onViewResults(project.id)}
                  >
                    View Latest Results
                  </Button>
                )} */}
                {onViewProjectDetails && (
                  <Button
                    variant="outlined"
                    color="primary"
                    className="pc-btn-secondary"
                    onClick={() => onViewProjectDetails(project.id)}
                  >
                    Project Details
                  </Button>
                )}
                {isExecutionInProgress ? (
                  <Button
                    variant="contained"
                    color="primary"
                    className="pc-btn-tinted"
                    disabled={!isExecutionInProgress}
                    startIcon={<VisibilityIcon />}
                    onClick={() =>
                      onViewCurrentStatus(
                        project.id,
                        project.name,
                        project.transactionId!,
                      )
                    }
                  >
                    View Current Status
                  </Button>
                ) : hasLastRunData ? (
                  <TestExecutionActionDropdown
                    buttonClassName="pc-btn-tinted pc-action-dropdown-trigger"
                    menuClassName="pc-action-dropdown-menu"
                    menuItemClassName="pc-action-dropdown-item"
                    defaultSelectedKey="RUN"
                    disabled={disableGenerateButton}
                    isExecutionInProgress={isExecutionInProgress}
                    options={options}
                    onSelect={handleActionSelect}
                  />
                ) : (
                  <Button
                    variant="contained"
                    className="pc-btn-tinted"
                    disabled={disableGenerateButton}
                    startIcon={<PlayCircleFilledIcon />}
                    onClick={() => onGenerateTests(project.id)}
                  >
                    Generate & Execute Tests
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Enhance Test Plan Modal */}
      <EnhanceTestPlanModal
        isToolAdmin={isToolAdmin}
        open={showEnhanceModal}
        onClose={() => setShowEnhanceModal(false)}
        onRunTests={onRunTests}
        project={project}
      />
    </Box>
  );
};

export const IqeProjectCard = memo(ProjectCard);

interface CardActionIconButtonProps {
  title: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}

const CardActionIconButton = ({
  title,
  disabled,
  icon,
  onClick,
}: CardActionIconButtonProps) => (
  <IconButton
    size="small"
    className="pc-icon-btn"
    onClick={onClick}
    title={title}
    aria-label={title}
    disabled={disabled}
  >
    {icon}
  </IconButton>
);
